interface VolumeGeometry {
  dimensions: ArrayLike<number>;
  spacing: ArrayLike<number>;
  origin: ArrayLike<number>;
  direction: ArrayLike<number>;
}

/** Never copy a mask onto a different grid, even if voxel counts match. */
export function assertMatchingPetGeometry(
  base: VolumeGeometry,
  mask: VolumeGeometry,
): void {
  for (const key of ["dimensions", "spacing", "origin", "direction"] as const) {
    const a = base[key];
    const b = mask[key];
    const length = key === "direction" ? 9 : 3;
    if (
      !a ||
      !b ||
      a.length !== length ||
      b.length !== length ||
      Array.from(a).some(
        (v, i) =>
          !Number.isFinite(v) ||
          !Number.isFinite(b[i]) ||
          Math.abs(v - b[i]) > (key === "dimensions" ? 0 : 0.0001),
      )
    ) {
      throw new Error(
        "This mask is not aligned with the selected PET image. Overlay was not applied.",
      );
    }
  }
}

/** PET/SUV need a data-derived window; the loader's MR window hides low uptake. */
export function petDisplayRange(values: ArrayLike<number>): {
  lower: number;
  upper: number;
} {
  const samples: number[] = [];
  const stride = Math.max(1, Math.floor(values.length / 100000));
  for (let i = 0; i < values.length; i += stride) {
    if (Number.isFinite(values[i]) && values[i] > 0) samples.push(values[i]);
  }
  samples.sort((a, b) => a - b);
  return {
    lower: 0,
    upper: samples.length
      ? Math.max(0.01, samples[Math.floor((samples.length - 1) * 0.995)])
      : 1,
  };
}

/** Cornerstone sorts slices in patient space, which may reverse file order. */
export function orderPetMaskVoxels(
  values: ArrayLike<number>,
  dimensions: ArrayLike<number>,
  imageIds: string[],
): Uint8Array {
  const [nx, ny, nz] = Array.from(dimensions);
  if (values.length !== nx * ny * nz || imageIds.length !== nz) {
    throw new Error("PET mask dimensions do not match the image.");
  }
  const result = new Uint8Array(values.length);
  const frames = new Set<number>();
  imageIds.forEach((id, outputFrame) => {
    const match = id.match(/[?&]frame=(\d+)(?:&|$)/);
    const inputFrame = match ? Number(match[1]) : -1;
    if (inputFrame < 0 || inputFrame >= nz || frames.has(inputFrame)) {
      throw new Error("Could not determine the PET mask slice order.");
    }
    frames.add(inputFrame);
    for (let i = 0; i < nx * ny; i++) {
      const value = values[inputFrame * nx * ny + i];
      if (value !== 0 && value !== 1)
        throw new Error("Expected a binary PET region mask.");
      result[outputFrame * nx * ny + i] = value;
    }
  });
  return result;
}
